
import React, { memo, useCallback, useState, useEffect } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from '@/components/ui/use-toast';
import DeleteAllChatsButton from '@/components/chat/DeleteAllChatsButton';

interface ChatPageHeaderProps {
  onNewChat: () => void;
}

const ChatPageHeader = memo(({ onNewChat }: ChatPageHeaderProps) => {
  const isMobile = useIsMobile();
  const [isCreating, setIsCreating] = useState(false);
  
  // Reset isCreating state after timeout (failsafe)
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (isCreating) {
      timer = setTimeout(() => {
        setIsCreating(false);
      }, 10000); // 10 second timeout
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isCreating]);
  
  // Use a stable callback reference
  const handleNewChat = useCallback(() => {
    if (isCreating) return;
    
    setIsCreating(true);
    try {
      onNewChat();
    } catch (error) {
      console.error('Error creating new chat:', error);
      toast({
        title: "Error creating chat",
        description: "There was an error creating a new chat. Please try again.",
        variant: "destructive"
      });
      setIsCreating(false);
    }
  }, [onNewChat, isCreating]);

  return (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Your Chats
        </h1>
        <p className="text-muted-foreground mt-1">
          Browse, search and manage your conversations
        </p>
      </div>
      
      <div className="flex items-center gap-2">
        <DeleteAllChatsButton />
        
        <Button 
          size="lg" 
          className="gap-2"
          onClick={handleNewChat}
          type="button"
          disabled={isCreating}
        >
          {isCreating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus size={18} />
          )}
          {isCreating ? "Creating..." : "New Chat"}
        </Button>
      </div>
    </div>
  );
});

ChatPageHeader.displayName = 'ChatPageHeader';

export default ChatPageHeader;
